import React, { useState, createContext, useContext } from 'react';

const AuthContext = createContext();

// A corrupted stored value must never crash the app on boot — drop it instead.
const readStoredJson = (key) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => readStoredJson('user'));

  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [impersonation, setImpersonation] = useState(() => readStoredJson('impersonation'));

  const login = (newUser, newToken) => {
    setUser(newUser);
    setToken(newToken);
    setImpersonation(null);
    localStorage.setItem('user', JSON.stringify(newUser));
    localStorage.setItem('token', newToken);
    localStorage.removeItem('impersonation');
  };

  const startImpersonation = ({ user: partnerUser, token: partnerToken, adminUser }) => {
    const original = {
      adminUser: adminUser || user,
      adminToken: token,
      partnerUser
    };
    setUser(partnerUser);
    setToken(partnerToken);
    setImpersonation(original);
    localStorage.setItem('user', JSON.stringify(partnerUser));
    localStorage.setItem('token', partnerToken);
    localStorage.setItem('impersonation', JSON.stringify(original));
  };

  const stopImpersonation = () => {
    if (!impersonation?.adminToken || !impersonation?.adminUser) {
      return;
    }

    setUser(impersonation.adminUser);
    setToken(impersonation.adminToken);
    setImpersonation(null);
    localStorage.setItem('user', JSON.stringify(impersonation.adminUser));
    localStorage.setItem('token', impersonation.adminToken);
    localStorage.removeItem('impersonation');
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setImpersonation(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('impersonation');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, impersonation, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
